import { ScenarioStatus } from 'context-system-model';
import { Macro } from 'macros-model';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, InputSearch, showNotification, Tag, TaptapList, useQueryFetch } from 'sonata-react-components';
import { getContextualMacroTagsFromAds } from 'sonata-static-storage/lib/helpers/macros.helpers';
import { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';

import { getCampaignMacrosStatuses, getMacros, getMacrosFile } from '@/services/contextualMacros.service';
import './ContextualMacros.scss';

export type MacroStatus =
  | ScenarioStatus.processing
  | ScenarioStatus.deleted
  | ScenarioStatus.failed
  | ScenarioStatus.active
  | 'draft';

type TaptapListItem = {
  creativesApplied: string;
  category: string;
  id: string;
  macroId: string;
  macroStatus: MacroStatus;
  macroStatusText: string;
  macroText: string;
  source: string;
  title: string;
  type: string;
};

type Props = {
  ads: AdInterface[];
  countries: string[];
  id?: string;
  entityId?: string;
  entityIsActive?: boolean;
  onAdsChange?: (ads: AdInterface[]) => void;
};

type State = {
  search: string | null;
  macrosStatuses?: Record<Macro['macroId'], any>;
  macrosStatusesLoaded?: boolean;
};

function processDetectedMacros(
  detectedMacros: Record<string, string[]>,
  macros: Record<string, any>[]
): Record<string, any>[] {
  return Object.entries(detectedMacros).reduce((acc: Record<string, any>[], [adId, macroIds]) => {
    macroIds.forEach((newMacroId: string) => {
      const existingMacro = acc.find(({ macroId }) => macroId === newMacroId);
      if (existingMacro) {
        existingMacro.adsIds = [...new Set((existingMacro.adsIds || []).concat(adId))];
      } else {
        const newContextualMacro = macros.find(({ macroId }) => macroId === newMacroId);
        if (newContextualMacro) {
          acc.push({
            ...newContextualMacro,
            adsIds: [adId]
          });
        }
      }
    });
    return acc;
  }, []);
}

export function ContextualMacros({ ads, countries, id, entityId, entityIsActive, onAdsChange }: Props) {
  const { formatMessage } = useIntl();
  const downloadFileToken: any = {};
  const getStatusToken: any = {};
  const macrosToken: any = {};

  const [{ search }, setState] = useState<State>({
    search: null
  });
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const { data: allMacros = [] } = useQueryFetch(['contextualMacros', countries], async () => {
    const response: any = await getMacros(countries, [], undefined as any, macrosToken);
    return (response || []).filter((m: any) => !m.internalUseOnly);
  });

  const contexTualMacrosFromAds = getContextualMacroTagsFromAds(ads, allMacros);
  const contextualMacros = processDetectedMacros(contexTualMacrosFromAds, allMacros as Record<string, Macro>[]);

  const getMacrosAds = (contextualMacrosList: Record<string, any>[]) => {
    const adMacrosMap: Record<string, { dataBlockVariableId?: string; macroId: string; type?: string }[]> = {};
    contextualMacrosList.forEach(({ adsIds = [], dataBlockVariableId, macroId, type }) => {
      adsIds.forEach((id: string) => {
        const adId = String(id);
        const list = adMacrosMap[adId] || (adMacrosMap[adId] = []);
        if (!list.find(m => m.macroId === macroId)) {
          list.push({ dataBlockVariableId, macroId, type });
        }
      });
    });
    return adMacrosMap;
  };

  const adMacrosMap = getMacrosAds(contextualMacros);
  const macrosKey = JSON.stringify(
    Object.entries(adMacrosMap)
      .map(([adId, list]) => ({ adId, macroIds: list.map(m => m.macroId).sort() }))
      .sort((a, b) => a.adId.localeCompare(b.adId))
  );
  const prevMacrosKeyRef = useRef<string>('');

  useEffect(() => {
    if (
      !onAdsChange ||
      (!contextualMacros.length && prevMacrosKeyRef.current === macrosKey) ||
      macrosKey === prevMacrosKeyRef.current
    ) {
      return;
    }
    prevMacrosKeyRef.current = macrosKey;

    const updatedAds = ads.map(ad => {
      const adId = String(ad._id);
      const associatedContextualMacros = adMacrosMap[adId] || [];
      return { ...ad, macros: associatedContextualMacros } as AdInterface;
    });
    onAdsChange(updatedAds);
  }, [macrosKey, onAdsChange, ads]);

  const normalizedSearch = (search || '').toLowerCase();
  const filteredMacros = contextualMacros.filter(m =>
    [m.humanReadableName, m.dataType, m.category, m.source]
      .filter(Boolean)
      .some(val => String(val).toLowerCase().includes(normalizedSearch))
  );

  const handleSearchChange = (value: string) => {
    setState(prevState => ({
      ...prevState,
      search: value
    }));
  };

  const {
    isLoading: isDownloading,
    refetch: refetchDownload,
    status: downloadStatus
  } = useQueryFetch(
    ['contextualMacrosDownload', entityId],
    async () => {
      const macrosData = contextualMacros.map(({ adsIds, macroId }) => ({
        appliedIn: (adsIds || []).length,
        macroId
      }));
      const macrosFile: any = await getMacrosFile(undefined, entityId!, macrosData, undefined, downloadFileToken);

      if (macrosFile?.urlFile) {
        window.open(macrosFile.urlFile);
      }
      return macrosFile;
    },
    {
      enabled: false
    }
  );

  const ids = contextualMacros.map(({ macroId }) => macroId);
  const macroIdsKey = [...new Set(ids)].sort().join('|');

  const { data: macrosStatuses = {}, status: macrosStatusesStatus } = useQueryFetch(
    ['contextualMacrosStatuses', entityId, macroIdsKey, entityIsActive],
    async () => {
      const macroIds = contextualMacros.map(({ macroId }) => macroId);
      if (!macroIds.length) {
        return {};
      }

      let newMacrosStatuses: Record<Macro['macroId'], any> = {};

      if (entityIsActive && entityId) {
        newMacrosStatuses = await getCampaignMacrosStatuses(entityId, macroIds.join(), getStatusToken);
      }

      macroIds.forEach(macroId => {
        if (newMacrosStatuses[macroId] == null) {
          newMacrosStatuses[macroId] = 'draft';
        }
      });
      setState(prev => ({
        ...prev,
        macrosStatuses: newMacrosStatuses || {},
        macrosStatusesLoaded: true
      }));
      return newMacrosStatuses || {};
    },
    {
      enabled: Boolean(contextualMacros.length)
    }
  );

  useEffect(() => {
    if (downloadStatus === 'error') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Unable to downloaded detected macros, please try again later',
          id: 'contextualMacros.downloadError'
        })
      );
    }
    if (macrosStatusesStatus === 'error') {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Unable to load macros statuses, please try again later',
          id: 'contextualMacros.statusesError'
        })
      );
    }
  }, [downloadStatus, macrosStatusesStatus]);

  const upperCaseFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  const compositionData = filteredMacros.map(macro => {
    return {
      category: macro.category,
      creativesApplied: formatMessage(
        {
          defaultMessage: '{count} Creatives',
          id: 'contextualMacros.table.creativesApplied'
        },
        { count: (macro.adsIds || []).length }
      ),
      id: `macro-${macro.macroId}`,
      macroStatus: (macrosStatuses as Record<string, any>)[macro.macroId] || 'draft',
      macroStatusText: upperCaseFirst((macrosStatuses as Record<string, any>)[macro.macroId] || 'draft'),
      macroText: `%${macro.macroId}%`,
      source: upperCaseFirst(macro.source),
      title: macro.humanReadableName,
      type: upperCaseFirst(macro.dataType)
    };
  });

  return (
    <div className="contextualMacros">
      <div className="contextualMacros__box" id={`creatives_adCreatives_box-${id}`}>
        <header className="contextualMacros__header">
          <ButtonUI
            className="contextualMacros__toggle"
            icon={`fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}
            id={`contextualMacros-toggle-${id}`}
            onClick={() => setIsOpen(prev => !prev)}
            shape="squareRounded"
            size="ultraSmall"
            type="tertiary"
          />
          <span className="contextualMacros__title">
            {formatMessage({
              defaultMessage: 'Contextual Macros',
              id: 'contextualMacros.title'
            })}
          </span>
        </header>
        {isOpen && (
          <div className="contextualMacros__content">
            <div className="contextualMacros__body">
              <InputSearch
                className="contextualMacros__inputSearch"
                id={`contextualMacros-searchByName-${id}`}
                lens
                name="contextualMacros"
                onChange={handleSearchChange}
                placeholder={formatMessage({
                  defaultMessage: 'Search Macro...',
                  id: 'contextualMacros.table.filter.search'
                })}
                tiny
              />
              <ButtonUI
                className="contextualMacros__downloadButton"
                disabled={contextualMacros.length === 0}
                icon="far fa-arrow-to-bottom"
                iconPosition="left"
                id={`downloadMacrosButton-${id}`}
                loading={isDownloading}
                onClick={() => refetchDownload()}
                size="ultraSmall"
                text={formatMessage({
                  defaultMessage: 'Download this list (.xlsx)',
                  id: 'contextualMacros.downloadList'
                })}
                type="tertiary"
              />
            </div>
            <div className="contextualMacros__contentTable">
              <TaptapList className="contextualMacros__list" data={compositionData} id={`contextualMacros-list-${id}`}>
                <TaptapList.ColumnsBar>
                  <TaptapList.ColumnsBar.Column
                    className="variableName"
                    description={formatMessage({
                      defaultMessage: 'Variable Name',
                      id: 'contextualMacros.table.columns.name'
                    })}
                    id="name"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="type"
                    description={formatMessage({
                      defaultMessage: 'Data Type',
                      id: 'contextualMacros.table.columns.type'
                    })}
                    id="type"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="category"
                    description={formatMessage({
                      defaultMessage: 'Category',
                      id: 'contextualMacros.table.columns.category'
                    })}
                    id="category"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="source"
                    description={formatMessage({
                      defaultMessage: 'Source',
                      id: 'contextualMacros.table.columns.source'
                    })}
                    id="source"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="appliedIn"
                    description={formatMessage({
                      defaultMessage: 'Applied in',
                      id: 'contextualMacros.table.columns.appliedIn'
                    })}
                    id="appliedIn"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="status"
                    description={formatMessage({
                      defaultMessage: 'Status',
                      id: 'contextualMacros.table.columns.status'
                    })}
                    id="status"
                  />
                  <TaptapList.ColumnsBar.Column
                    className="macroId"
                    description={formatMessage({
                      defaultMessage: 'Macro ID',
                      id: 'contextualMacros.table.columns.macroId'
                    })}
                    id="macroId"
                  />
                </TaptapList.ColumnsBar>
                <TaptapList.RowsList<TaptapListItem>
                  emptyStateDescription={formatMessage({
                    defaultMessage: 'Try adjusting your search or filters to find macros.',
                    id: 'contextualMacros.emptyState.description'
                  })}
                  emptyStateIcon="fal fa-empty-set"
                  emptyStateTitle={formatMessage({
                    defaultMessage: 'No results match your filters',
                    id: 'contextualMacros.emptyState.title'
                  })}
                >
                  {itemsList =>
                    itemsList.map(item => (
                      <TaptapList.RowsList.Row id={item.id} key={item.id}>
                        <span className="variableName">{item.title}</span>
                        <span className="type">{item.type}</span>
                        <span className="category">{item.category}</span>
                        <span className="source">{item.source}</span>
                        <span className="appliedIn">
                          <Tag id={`appliedInTag${item.id}`} status="default">
                            {item.creativesApplied}
                          </Tag>
                        </span>
                        <span className="status">
                          <Tag
                            customColors={{
                              background:
                                item.macroStatus === ScenarioStatus.active ? 'rgba(54,224,224,0.3)' : '#A0B2FF26',
                              text: item.macroStatus === ScenarioStatus.active ? '#198989' : '#617EB5'
                            }}
                            id={`${item.id}-statusTag`}
                            status="custom"
                          >
                            {item.macroStatus !== ScenarioStatus.active && (
                              <span className="statusLoading fad fa-spinner" />
                            )}
                            {item.macroStatusText}
                          </Tag>
                        </span>
                        <span className="macroId">
                          <ButtonUI
                            className="macroId__Button"
                            icon={'fal fa-copy'}
                            id={id}
                            onClick={() => {
                              navigator.clipboard.writeText(`%${item.macroText}%`);
                              showNotification(
                                'success',
                                formatMessage({
                                  defaultMessage: 'MacroId was successfully copied',
                                  id: 'contextualMacros.macroId.copiedNotification'
                                })
                              );
                            }}
                            shape="link"
                            size="ultraSmall"
                            type="secondary"
                          />
                          {item.macroText}
                        </span>
                      </TaptapList.RowsList.Row>
                    ))
                  }
                </TaptapList.RowsList>
              </TaptapList>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
